// Zentrale Modul-Registry für das modulare Lizenzmodell (TechSoul Work).
// EINE Quelle der Wahrheit für Module, Tarife und Preise.
// Backend-Spiegelung: functions/shared/moduleRegistry.js — synchron halten!
//
// Regel 1: Nur echte Feature-IDs aus featureRegistry.js werden gemappt.
// Regel 2: Life-Features (bmi_rechner, diat_rechner, rezept_generator,
//          voice_cart, job_kompass) bleiben AUSSERHALB des Modulmodells
//          und werden weiterhin über enabledFeatures verwaltet.

import { FEATURES, FEATURE_REGISTRY } from './featureRegistry';

// Grundausstattung: tarifunabhängig für jeden eingeloggten Nutzer.
export const BASE_FEATURES = [
  FEATURES.IDEEN_EINREICHEN,
  FEATURES.SUPPORT_DEVELOPER,
];

// Feature-Labor: Features mit lab-Flag in der Registry (Opt-in, kostenlos,
// keinem Modul zugeordnet). Zugriff über userSettings.advanced.labFeaturesEnabled.
export const LAB_FEATURES = FEATURE_REGISTRY.filter((f) => f.lab).map((f) => f.id);

export const MODULES = {
  leadership_assistant: {
    key:              'leadership_assistant',
    route:            '/work',
    name:             'Leadership Assistant',
    icon:             '🤖',
    description:      'Dein persönlicher KI-Führungsassistent – Tagesbriefing, Entscheidungen, Sprachsteuerung.',
    mainFunctions:    ['Leadership Coach mit Tagesbriefing', 'Sprachein- und -ausgabe des Assistenten', 'Proaktive Rückfragen & Plattformhilfe', 'Humor-Modus & Persönlichkeiten'],
    includedFeatures: [FEATURES.FRAG_CHRISTIAN, FEATURES.ATLAS_SPRACHE],
    pricePerMonth:    null, // Basismodul – in Starter/Professional enthalten
    status:           'available',
  },
  meetings: {
    key:              'meetings',
    route:            '/work/meetings',
    name:             'Meetings',
    icon:             '📅',
    description:      'Meetings vorbereiten, aufzeichnen und auswerten – Aufgaben und Entscheidungen automatisch erfassen.',
    mainFunctions:    ['Meeting-Vorbereitung mit Agenda', 'Meeting-Analyse aus Transkripten', 'Audio-Transkription', 'Meeting-Hub mit Verlauf'],
    includedFeatures: [FEATURES.MEETING_VORBEREITUNG, FEATURES.MEETING_ANALYSE, FEATURES.AUDIO_TRANSKRIPTION],
    pricePerMonth:    8,
    status:           'available',
  },
  execution: {
    key:              'execution',
    route:            '/work/delegation',
    name:             'Execution',
    icon:             '📤',
    description:      'Aufgaben delegieren, verfolgen und sauber übergeben – dein operatives Cockpit.',
    mainFunctions:    ['Aufgabenübergabe mit Standards', 'Delegation Center', 'TaskBoard im Cockpit'],
    includedFeatures: [FEATURES.AUFGABEN_UEBERGABE],
    pricePerMonth:    8,
    status:           'available',
  },
  thinking: {
    key:              'thinking',
    route:            '/work',
    name:             'Thinking',
    icon:             '🧠',
    description:      'Denkwerkzeuge für klare Entscheidungen – strukturieren, analysieren, formulieren.',
    mainFunctions:    ['Gedanken-Sortierer & Vergessens-Checker', 'Entscheidungs-Helfer & Problem Analyzer', 'SOP-Generator & Einfach-Erklärer', 'Antwort-Generator'],
    includedFeatures: [
      FEATURES.GEDANKEN_SORTIERER, FEATURES.VERGESSENS_CHECKER, FEATURES.ENTSCHEIDUNGS_HELFER,
      FEATURES.PROBLEM_ANALYZER, FEATURES.EINFACH_ERKLAERER, FEATURES.SOP_GENERATOR,
      FEATURES.ANTWORT_GENERATOR,
    ],
    pricePerMonth:    8,
    status:           'available',
  },
  organizational_memory: {
    key:              'organizational_memory',
    route:            '/work/memory',
    name:             'Organizational Memory',
    icon:             '🗄️',
    description:      'Dein Wissensarchiv – Meetings, Entscheidungen und Analysen dauerhaft durchsuchbar.',
    mainFunctions:    ['KI-Suche über dein Archiv', 'Memory-Modul im Work-Bereich', 'Entscheidungsregister'],
    includedFeatures: [FEATURES.MEMORY],
    pricePerMonth:    10,
    status:           'available',
  },
  team_leadership: {
    key:              'team_leadership',
    route:            '/work/team',
    name:             'Team Leadership',
    icon:             '👥',
    description:      'Dein Team im Blick – Organigramm, Auslastung und Team-Events.',
    mainFunctions:    ['Teamübersicht & Organigramm', 'Auslastungsanalyse', 'Team Event Planung'],
    includedFeatures: [FEATURES.TEAM_EVENT_PLANUNG],
    pricePerMonth:    10,
    status:           'available',
  },
  integrations: {
    key:              'integrations',
    route:            null,
    name:             'Integrations',
    icon:             '🔌',
    description:      'Anbindung externer Systeme – Kalender, E-Mail und mehr.',
    mainFunctions:    ['Bald verfügbar'],
    includedFeatures: [],
    pricePerMonth:    12,
    status:           'coming_soon',
  },
};

export const ALL_MODULE_KEYS = Object.keys(MODULES);

// Alle Work-Module außer Integrations (für Business-Tarif).
const BUSINESS_MODULES = ALL_MODULE_KEYS.filter((k) => k !== 'integrations');

export const PLANS = {
  starter: {
    key:                   'starter',
    name:                  'Starter',
    baseModules:           ['leadership_assistant'],
    selectableModuleLimit: 1,
    creditLimit:           300,
  },
  professional: {
    key:                   'professional',
    name:                  'Professional',
    baseModules:           ['leadership_assistant'],
    selectableModuleLimit: 3,
    creditLimit:           1000,
  },
  business: {
    key:             'business',
    name:            'Business',
    includedModules: BUSINESS_MODULES,
    creditLimit:     3000,
  },
  enterprise: {
    key:             'enterprise',
    name:            'Enterprise',
    includedModules: ALL_MODULE_KEYS,
    creditLimit:     null, // Fair Use
  },
};

export const PLAN_KEYS = Object.keys(PLANS);

// Dauer des einmaligen kostenlosen Modul-Tests.
export const MODULE_TRIAL_DAYS = 14;

// Gültige Abrechnungs-Status (account-weit; 'trial' existiert nicht mehr —
// Trials laufen ausschließlich pro Modul über moduleTrials).
// 'paused'/'expired' kommen aus der Paddle-Integration (subscription.paused
// bzw. endgültig abgelaufene Subscription); fehlender Status = kein Billing.
export const BILLING_STATUSES = ['active', 'past_due', 'cancelled', 'paused', 'expired'];

// ── Paddle-Billing (Entscheidungen 1–5 vom 05.07.2026) ────────────────

// Abrechnungsintervalle für Tarife UND Zusatzmodule (Module folgen dem
// Intervall der einen Subscription pro User).
export const BILLING_INTERVALS = ['monthly', 'quarterly', 'halfyearly', 'yearly'];

// Karenzzeit nach fehlgeschlagener Zahlung (past_due): Zugriff bleibt bis
// currentPeriodEnd + Karenz erhalten — keine sofortige Sperre.
export const GRACE_PERIOD_DAYS = 7;

// Paddle-Price-IDs werden NICHT hier gepflegt, sondern als Env-Variablen
// (functions/.env, Sandbox/Production getrennt). Dieses Mapping liefert pro
// Tarif/Modul und Intervall den Env-Variablennamen; aufgelöst wird
// ausschließlich serverseitig — das Frontend kennt keine Price-IDs.
// Enterprise hat bewusst keine Prices (kein Self-Service-Checkout),
// leadership_assistant ist nicht einzeln buchbar, integrations ist
// „Bald verfügbar" und (noch) ohne Prices.
function buildPriceEnvMap(kind, keys) {
  const map = {};
  for (const key of keys) {
    map[key] = {};
    for (const interval of BILLING_INTERVALS) {
      map[key][interval] = `PADDLE_PRICE_${kind}_${key.toUpperCase()}_${interval.toUpperCase()}`;
    }
  }
  return map;
}

export const PADDLE_PRICE_ENV_VARS = {
  plans:   buildPriceEnvMap('PLAN', ['starter', 'professional', 'business']),
  modules: buildPriceEnvMap(
    'MODULE',
    ALL_MODULE_KEYS.filter((k) => k !== 'leadership_assistant' && k !== 'integrations')
  ),
};
